// System: errors, the audit log, notices to every account holder, and settings, as tabs of one page.
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { fmtDateTime, useAction, useLoad, usePaged, useTitle } from '../lib';
import { Alert, Button, Card, Empty, Field, Json, Loading, Page, Pager, Search, TableCard } from '../ui';

const TABS = [{ to: '/system/errors', label: 'Errors' }, { to: '/system/audit', label: 'Audit log' }, { to: '/system/notices', label: 'Notices' }, { to: '/system/settings', label: 'Settings' }];
const SOURCES = ['', 'api', 'worker', 'stripe', 'app', 'site', 'admin'];

// ---------------------------------------------------------------- errors (self-hosted error tracking)
interface ErrorRow { id: number; at: string; source: string; level: string; message: string; stack: string | null; context: unknown }

export function Errors() {
  useTitle('Errors');
  const [p, setP] = useSearchParams();
  const source = p.get('source') ?? '';
  const list = usePaged<ErrorRow>(`/admin/errors${source ? `?source=${source}` : ''}`, 'errors');
  return (
    <Page title="System" tabs={TABS}>
      <TableCard
        toolbar={<span className="seg" role="group" aria-label="Source">{SOURCES.map((s) => <button key={s} type="button" aria-pressed={source === s} onClick={() => setP(s ? { source: s } : {})}>{s || 'All'}</button>)}</span>}
        pager={list.rows && <Pager offset={list.offset} total={list.total} shown={list.rows.length} onPage={list.setOffset} />}
      >
        <Alert>{list.error}</Alert>
        {!list.rows ? <Loading /> : !list.rows.length ? <Empty>No errors{source ? ` from ${source}` : ''}.</Empty> : (
          <table>
            <thead><tr><th>When</th><th>Source</th><th>Message</th></tr></thead>
            <tbody>
              {list.rows.map((e) => (
                <tr key={e.id}>
                  <td className="nowrap muted">{fmtDateTime(e.at)}</td>
                  <td><span className={`badge ${e.source === 'stripe' ? 'badge-warn' : ''}`}>{e.source}</span></td>
                  <td>
                    <div style={{ wordBreak: 'break-word' }}>{e.message}</div>
                    {e.stack && <details className="json-d"><summary>stack</summary><pre className="json">{e.stack}</pre></details>}
                    <Json value={e.context} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableCard>
    </Page>
  );
}

// ---------------------------------------------------------------- audit log
interface AuditRow { id: number; at: string; actor: string; action: string; target_type: string | null; target_id: string | null; detail: unknown; ip: string | null }

export function Audit() {
  useTitle('Audit log');
  const [p, setP] = useSearchParams();
  const q = p.get('q') ?? '';
  const list = usePaged<AuditRow>(`/admin/audit?q=${encodeURIComponent(q)}`, 'entries');
  const target = (e: AuditRow) =>
    e.target_type === 'account' ? <Link to={`/customers/${e.target_id}`}>account</Link>
      : e.target_type === 'order' ? <Link to={`/sales/orders/${e.target_id}`}>order</Link>
      : e.target_type ? <span className="small">{e.target_type} <span className="mono">{e.target_id}</span></span> : null;
  return (
    <Page title="System" tabs={TABS}>
      <TableCard
        toolbar={<Search value={q} onSearch={(v) => setP(v ? { q: v } : {})} placeholder="Actor, action or target id" />}
        pager={list.rows && <Pager offset={list.offset} total={list.total} shown={list.rows.length} onPage={list.setOffset} />}
      >
        <Alert>{list.error}</Alert>
        {!list.rows ? <Loading /> : !list.rows.length ? <Empty>Nothing found.</Empty> : (
          <table>
            <thead><tr><th>When</th><th>Who</th><th>Action</th><th>Target</th><th>IP</th></tr></thead>
            <tbody>
              {list.rows.map((e) => (
                <tr key={e.id}>
                  <td className="nowrap muted">{fmtDateTime(e.at)}</td>
                  <td className="small">{e.actor}</td>
                  <td><span className="mono">{e.action}</span><Json value={e.detail} /></td>
                  <td>{target(e)}</td>
                  <td className="mono muted">{e.ip}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TableCard>
    </Page>
  );
}

// ---------------------------------------------------------------- notices to every account holder
// The terms promise an email before changes that matter and 60 days' notice before a shutdown; the privacy
// policy promises the same for its changes. Not for marketing.
interface Notice { id: string; subject: string; sent_by: string; recipients: number; sent: number; failed: number; status: string; created_at: string }

export function Notices() {
  useTitle('Notices');
  const { data, error, reload } = useLoad(() => api.get<{ recipients: number; notices: Notice[] }>('/admin/notices'));
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const send = useAction(async () => {
    if (!confirm(`Send "${subject}" to ${data?.recipients} account holders now? This cannot be undone.`)) return;
    await api.post('/admin/notices', { subject, body });
    setSubject(''); setBody('');
    setMsg('Sending. The count updates as it goes.');
    await reload();
  });
  return (
    <Page title="System" tabs={TABS}>
      <Alert>{error}</Alert>
      {!data ? <Loading /> : (
        <div className="split2">
          <Card title="New notice" sub={`To every confirmed account holder (${data.recipients}). Only for changes to the terms or privacy policy, or a shutdown.`}>
            <form className="stack-sm" onSubmit={(e) => { e.preventDefault(); void send.run(); }}>
              <Field label="Subject" required maxLength={150} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Changes to the Math Ladder terms of service" />
              <div className="field">
                <label htmlFor="notice-body">Message</label>
                <textarea id="notice-body" className="input" rows={10} required maxLength={10000} value={body} onChange={(e) => setBody(e.target.value)}
                  placeholder={'Plain text. A blank line starts a new paragraph.\n\nSay what is changing, when, and where to read more.'} />
                <div className="hint">Ends with "You are receiving this because you have a Math Ladder account." Replies go to support@.</div>
              </div>
              <Alert>{send.error}</Alert>
              <Alert kind="good">{msg}</Alert>
              <Button className="btn btn-primary" busy={send.busy} disabled={subject.trim().length < 5 || body.trim().length < 20}>Send to {data.recipients} account holders</Button>
            </form>
          </Card>
          <TableCard toolbar={<strong className="small">Sent</strong>}>
            {!data.notices.length ? <Empty>No notices sent yet.</Empty> : (
              <table>
                <thead><tr><th>Date</th><th>Subject</th><th className="num">Sent</th><th>Status</th></tr></thead>
                <tbody>
                  {data.notices.map((n) => (
                    <tr key={n.id}>
                      <td className="nowrap muted">{fmtDateTime(n.created_at)}</td>
                      <td>{n.subject}<div className="small muted">{n.sent_by}</div></td>
                      <td className="num">{n.sent}/{n.recipients}{n.failed ? <div className="small" style={{ color: 'var(--bad)' }}>{n.failed} failed</div> : null}</td>
                      <td>{n.status === 'done' ? 'Done' : 'Sending…'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </TableCard>
        </div>
      )}
    </Page>
  );
}

// ---------------------------------------------------------------- settings
interface FairUse { sheets_per_student_per_day: number; books_per_student_per_30_days: number; free_sheets_per_ip_per_hour: number; free_sheets_per_ip_per_day: number; library_sheets_per_account_per_day: number; unit_packets_per_account_per_30_days: number }
// the free worksheets are static files now, so the per-IP limits are kept but not shown
const FAIR: [keyof FairUse, string, string][] = [
  ['library_sheets_per_account_per_day', 'Worksheets per account per day', 'A topic packet counts as 5'],
  ['unit_packets_per_account_per_30_days', 'Unit packets per account per 30 days', ''],
  ['sheets_per_student_per_day', 'Climb the ladder: sheets per learner per day', 'Every page of a day or week counts'],
  ['books_per_student_per_30_days', 'Climb the ladder: unit books per learner per 30 days', ''],
];

export function Settings() {
  useTitle('Settings');
  const s = useLoad(() => api.get<{ settings: { fair_use: FairUse; renewal_reminder_days: number[] } }>('/admin/settings'));
  return (
    <Page title="System" tabs={TABS}>
      <Alert>{s.error}</Alert>
      {!s.data ? <Loading /> : (
        <div className="split2" style={{ alignItems: 'start' }}>
          <FairUseForm value={s.data.settings.fair_use} />
          <Reminders value={s.data.settings.renewal_reminder_days} />
        </div>
      )}
    </Page>
  );
}

function FairUseForm({ value }: { value: FairUse }) {
  const [f, setF] = useState(value);
  const [ok, setOk] = useState(false);
  const save = useAction(async () => { await api.put('/admin/settings/fair_use', f); setOk(true); });
  return (
    <Card title="Fair use" sub="Limits on making new worksheets. Downloading is never limited.">
      <form className="stack-sm" onSubmit={(e) => { e.preventDefault(); void save.run(); }}>
        <div>
          {FAIR.map(([k, label, hint]) => (
            <div className="setting" key={k}>
              <label htmlFor={`fu-${k}`}>{label}</label>
              <input id={`fu-${k}`} className="input input-sm" type="number" min={0} required value={f[k]} onChange={(e) => { setOk(false); setF({ ...f, [k]: Number(e.target.value) }); }} />
              {hint && <span className="hint">{hint}</span>}
            </div>
          ))}
        </div>
        <Alert>{save.error}</Alert>
        <div className="row"><Button className="btn btn-primary btn-sm" busy={save.busy}>Save</Button>{ok && <span className="small" style={{ color: 'var(--good)' }}>Saved.</span>}</div>
      </form>
    </Card>
  );
}

function Reminders({ value }: { value: number[] }) {
  const [v, setV] = useState(value.join(', '));
  const [ok, setOk] = useState(false);
  const save = useAction(async () => {
    await api.put('/admin/settings/renewal_reminder_days', v.split(/[\s,]+/).filter(Boolean).map(Number));
    setOk(true);
  });
  return (
    <Card title="Plan ending reminders" sub="Emails before a yearly plan ends">
      <form className="stack-sm" onSubmit={(e) => { e.preventDefault(); void save.run(); }}>
        <div className="setting">
          <label htmlFor="reminder-days">Days before the end</label>
          <input id="reminder-days" className="input input-sm" value={v} onChange={(e) => { setOk(false); setV(e.target.value); }} />
          <span className="hint">Comma separated, up to 5. Blank for none.</span>
        </div>
        <Alert>{save.error}</Alert>
        <div className="row"><Button className="btn btn-primary btn-sm" busy={save.busy}>Save</Button>{ok && <span className="small" style={{ color: 'var(--good)' }}>Saved.</span>}</div>
      </form>
    </Card>
  );
}
